import { useEffect, useRef, useState } from 'react';
import type { Draft } from 'immer';
import { FOLIAGE_KINDS, FOLIAGE_LABELS, type Body, type BodyBehavior, type EdgeArtMode, type LevelDoc, type MovementVars, type PlacedObject, type TerrainShape, type Waypoint } from '../model/format.ts';
import { num } from '../model/unityProps.ts';
import { terrainStyles, validateShape } from '../terrain/index.ts';
import { terrainMissing } from '../model/bake.ts';
import { findObject, getContainer, openTileset, setActiveInput, useEditor } from './store.ts';
import { objectInfo, bodyBounds, movingOf } from './scene.ts';
import { FieldEditor } from './FieldEditor.tsx';
import { Check, ColorInput, Icon, NumberInput, Row, Section, Select, TextInput } from './widgets.tsx';
import { deleteSelection, detachToBody, duplicateObjects, findTerrain, newWaypoint } from './actions.ts';
import { draftObject } from './store.ts';
import { bodyComps, objectComps, writeField, type CompRef } from './components.ts';
import { controllersOf, entityNamer, linksTo, slotsOf } from './links.ts';
import { SCHEMAS } from './schema.ts';
import { CompFields, ControllerRows, LinkList, selectEntity } from './SimpleFields.tsx';
import { placementPose, prefabInfo } from './catalog.ts';
import { useSettings } from './settings.ts';
import { BEHAVIOR_LABELS, addController, behaviorLabel, bodyToLevel, recentreBody, setBehavior, shapeToBody } from './bodies.ts';
import { setSpriteSize } from './resize.ts';

export { entityNamer };

const SKIP_TYPES = new Set(['Transform', 'RectTransform', 'NetworkObject', 'CanvasRenderer', 'ParticleSystem', 'ParticleSystemRenderer', 'AudioSource', 'Animator', 'MeshRenderer', 'MeshFilter', 'TrailRenderer', 'Tilemap']);

export const SPEED_ENUM: [number, string][] = ['Default', 'Very slow', 'Slow', 'Regular', 'Fast', 'Very fast', 'Door', 'Launch', 'Launch hard'].map((n, i) => [i, n]);
export const ACC_ENUM: [number, string][] = ['Default', 'Slow', 'Regular', 'Door', 'Launch'].map((n, i) => [i, n]);
const ACTIVATION: [number, string][] = [[0, 'All activators are on'], [1, 'Any activator is on']];
const DEACTIVATION: [number, string][] = ['Keep going', 'Stop immediately', 'Stop at the next point', 'Go back to the previous point', 'Go back to the start'].map((n, i) => [i, n]);
const FINISH: [number, string][] = ['Stop for good', 'Stop until turned on again', 'Loop to the first point', 'Go back along the path'].map((n, i) => [i, n]);

export function Inspector() {
  const doc = useEditor((s) => s.doc);
  const sel = useEditor((s) => s.selection);
  if (!doc) return null;
  switch (sel.kind) {
    case 'objects':
      if (sel.ids.length === 1) return <ObjectInspector id={sel.ids[0]} doc={doc} />;
      return <MultiInspector ids={sel.ids} />;
    case 'body':
      return <BodyInspector id={sel.id} doc={doc} />;
    case 'terrain':
      return <TerrainInspector id={sel.id} doc={doc} />;
    case 'waypoint':
      return <WaypointInspector bodyId={sel.body} id={sel.id} doc={doc} />;
    case 'cells':
      return (
        <div className="panel-pad">
          <p>
            Cells {sel.rect[0]},{sel.rect[1]} → {sel.rect[2]},{sel.rect[3]}
          </p>
          <button className="btn" onClick={() => detachToBody(sel.rect)}>Detach to moving platform</button>{' '}
          <button className="btn" onClick={() => detachToBody(sel.rect, 'fadeOut')}>Detach to fade-out block</button>{' '}
          <button className="btn danger" onClick={() => deleteSelection(sel)}>Clear cells</button>
        </div>
      );
    default:
      return (
        <div className="panel-pad muted">
          Select an object, moving platform, terrain shape or waypoint. Tap empty space and drag to box-select objects. Right-click (or long-press) a terrain shape to turn it into a moving platform.
        </div>
      );
  }
}

function MultiInspector({ ids }: { ids: string[] }) {
  return (
    <div className="panel-pad">
      <p>{ids.length} objects selected</p>
      <button className="btn" onClick={() => useEditor.getState().set({ selection: { kind: 'objects', ids: duplicateObjects(ids) } })}>Duplicate</button>{' '}
      <button className="btn danger" onClick={() => deleteSelection({ kind: 'objects', ids })}>Delete</button>
    </div>
  );
}

function compLabel(c: CompRef) {
  return c.child ? `${c.child} › ${c.type}` : c.type;
}

/** What links to an entity, e.g. the platforms a switch drives. */
function LinkedFrom({ doc, ent }: { doc: LevelDoc; ent: string }) {
  const rows = linksTo(doc, ent);
  if (!rows.length) return null;
  const name = entityNamer(doc);
  return (
    <Section title={`Linked to (${rows.length})`}>
      {rows.map((r, i) => (
        <div key={i} className="row">
          <button className="ref linkish" onClick={() => selectEntity(r.from)}>{name(r.from)}</button>
          <span className="row-value muted small">{r.label}</span>
        </div>
      ))}
    </Section>
  );
}

function ObjectInspector({ id, doc }: { id: string; doc: LevelDoc }) {
  const change = useEditor((s) => s.change);
  const advanced = useSettings((s) => s.advanced);
  const f = findObject(doc, id);
  if (!f) return <div className="panel-pad muted">Object not found</div>;
  const o = f.obj;
  const info = objectInfo(doc, o);
  const name = entityNamer(doc);
  const pinfo = prefabInfo(o.prefab);
  const upd = (label: string, fn: (o: Draft<PlacedObject>) => void) => change(label, (d) => { const x = draftObject(d, id); if (x) fn(x); });
  const comps = objectComps(doc, o);
  const curated = comps.filter((c) => SCHEMAS[c.type]?.fields.length);
  const seen = new Set<string>();
  const curatedUnique = curated.filter((c) => (seen.has(c.type) ? false : (seen.add(c.type), true)));
  const owned = doc.bodies.filter((b) => b.owner?.object === id);
  const tk = pinfo.transform ?? 'full';
  const authored = placementPose(o.prefab);
  const showRot = advanced || tk === 'rotate' || tk === 'full' || o.rotation !== authored.rotation;
  const showScale = advanced || tk === 'full' || (tk !== 'zone' && (o.scale[0] !== authored.scale[0] || o.scale[1] !== authored.scale[1]));
  const rootSprite = comps.find((c) => c.type === 'SpriteRenderer' && !c.child && num(c.data?.m_DrawMode) !== 0);
  const setSize = (ax: 'x' | 'y', v: number) =>
    change('Resize', (d) => {
      if (!rootSprite) return;
      const cur = [num(rootSprite.data.m_Size?.x, 1), num(rootSprite.data.m_Size?.y, 1)];
      setSpriteSize(d, id, ax === 'x' ? v : cur[0], ax === 'y' ? v : cur[1]);
    });
  const raw = comps
    .filter((c) => !SKIP_TYPES.has(c.type))
    .sort((a, b) => Number(!b.child) - Number(!a.child) || Number(!!SCHEMAS[b.type]) - Number(!!SCHEMAS[a.type]));
  return (
    <div className="inspector">
      <div className="insp-head">
        <TextInput value={o.name} onChange={(v) => upd('Rename', (x) => { x.name = v; })} />
        <span className="muted small">{pinfo.label}{f.body ? ` · rides ${f.body.name}` : ''}</span>
      </div>
      <Section title="Placement">
        <Row label="Position">
          <span className="vec">
            <NumberInput value={o.x} step={0.5} width={70} onChange={(v) => upd('Move', (x) => { x.x = v; })} />
            <NumberInput value={o.y} step={0.5} width={70} onChange={(v) => upd('Move', (x) => { x.y = v; })} />
          </span>
        </Row>
        {showRot && (
          <Row label="Rotation">
            <NumberInput value={o.rotation} step={15} onChange={(v) => upd('Rotate', (x) => { x.rotation = v; })} />
          </Row>
        )}
        {tk === 'zone' && !advanced && (
          <Row label="Size">
            <span className="vec">
              <NumberInput value={o.scale[0]} step={0.5} width={70} onChange={(v) => upd('Resize', (x) => { x.scale = [v, x.scale[1]]; })} />
              <NumberInput value={o.scale[1]} step={0.5} width={70} onChange={(v) => upd('Resize', (x) => { x.scale = [x.scale[0], v]; })} />
            </span>
          </Row>
        )}
        {rootSprite && (
          <Row label="Size" title="Tiled sprite size">
            <span className="vec">
              <NumberInput value={num(rootSprite.data.m_Size?.x, 1)} step={1} min={0.1} width={70} onChange={(v) => setSize('x', v)} />
              <NumberInput value={num(rootSprite.data.m_Size?.y, 1)} step={1} min={0.1} width={70} onChange={(v) => setSize('y', v)} />
            </span>
          </Row>
        )}
        {showScale && (
          <Row label="Scale">
            <span className="vec">
              <NumberInput value={o.scale[0]} step={0.25} width={70} onChange={(v) => upd('Scale', (x) => { x.scale = [v, x.scale[1]]; })} />
              <NumberInput value={o.scale[1]} step={0.25} width={70} onChange={(v) => upd('Scale', (x) => { x.scale = [x.scale[0], v]; })} />
            </span>
          </Row>
        )}
        {(advanced || o.active === false) && (
          <Row label="Active">
            <Check value={o.active !== false} onChange={(v) => upd('Toggle active', (x) => { if (v) delete x.active; else x.active = false; })} />
          </Row>
        )}
        {advanced && (
          <Row label="Folder">
            <TextInput value={o.folder ?? ''} placeholder="e.g. Level/Coins" onChange={(v) => upd('Set folder', (x) => { if (v) x.folder = v; else delete x.folder; })} />
          </Row>
        )}
      </Section>
      {owned.length > 0 && (
        <Section title="Moving parts">
          {owned.map((b) => (
            <button key={b.id} className="btn block" onClick={() => useEditor.getState().set({ selection: { kind: 'body', id: b.id } })}>
              {b.name} <span className="muted small">· {behaviorLabel(b.behavior)}</span>
            </button>
          ))}
        </Section>
      )}
      {curatedUnique.map((c) => (
        <Section key={`${c.type}-${JSON.stringify(c.addr)}`} title={SCHEMAS[c.type].title}>
          <CompFields doc={doc} comp={c} />
        </Section>
      ))}
      {!advanced && !curatedUnique.length && !owned.length && <div className="panel-pad muted small">This object has no settings. Turn on “Show raw Unity fields” in Settings to edit everything.</div>}
      <LinkedFrom doc={doc} ent={`obj:${id}`} />
      {advanced && (
        <>
          <div className="adv-head">Raw Unity fields</div>
          {raw.map((c) => (
            <Section key={JSON.stringify(c.addr)} title={compLabel(c)} open={false}>
              <FieldEditor
                data={c.data}
                entityName={name}
                overridden={c.addr.kind === 'objMod' ? (p) => o.mods.some((m) => m.t === (c.addr as { t: string }).t && m.p === p) : undefined}
                onSet={(p, v) => change(`Edit ${c.type}`, (d) => writeField(d, c.addr, p, v))}
              />
            </Section>
          ))}
        </>
      )}
      <div className="insp-actions">
        {o.prefab && o.mods.length > 0 && (
          <button className="btn" onClick={() => upd('Reset overrides', (x) => { x.mods = []; })}>Reset {o.mods.length} changes</button>
        )}
        <button className="btn" onClick={() => useEditor.getState().set({ selection: { kind: 'objects', ids: duplicateObjects([id]) } })}>Duplicate</button>
        <button className="btn danger" onClick={() => deleteSelection({ kind: 'objects', ids: [id] })}>Delete</button>
      </div>
    </div>
  );
}

function VarsEditor({ vars, onChange, showReturn = true }: { vars: MovementVars; onChange: (k: keyof MovementVars, v: number) => void; showReturn?: boolean }) {
  return (
    <>
      <Row label="Speed">
        <Select value={vars.speedEnum} options={SPEED_ENUM} onChange={(v) => onChange('speedEnum', v)} />
      </Row>
      <Row label="Custom speed" title="Tiles per second; 0 = use the speed above">
        <NumberInput value={vars.speedOverride} step={0.5} min={0} onChange={(v) => onChange('speedOverride', v)} />
      </Row>
      <Row label="Smoothing">
        <Select value={vars.accEnum} options={ACC_ENUM} onChange={(v) => onChange('accEnum', v)} />
      </Row>
      <Row label="Custom smoothing" title="Seconds to reach full speed (SmoothDamp time); 0 = use the setting above">
        <NumberInput value={vars.accOverride} step={0.05} min={0} onChange={(v) => onChange('accOverride', v)} />
      </Row>
      {showReturn && (
        <Section title="Return trip" open={false}>
          <Row label="Speed">
            <Select value={vars.returnSpeedEnum} options={SPEED_ENUM} onChange={(v) => onChange('returnSpeedEnum', v)} />
          </Row>
          <Row label="Custom speed">
            <NumberInput value={vars.returnSpeedOverride} step={0.5} min={0} onChange={(v) => onChange('returnSpeedOverride', v)} />
          </Row>
          <Row label="Smoothing">
            <Select value={vars.returnAccEnum} options={ACC_ENUM} onChange={(v) => onChange('returnAccEnum', v)} />
          </Row>
          <Row label="Custom smoothing">
            <NumberInput value={vars.returnAccOverride} step={0.05} min={0} onChange={(v) => onChange('returnAccOverride', v)} />
          </Row>
        </Section>
      )}
    </>
  );
}

function BodyInspector({ id, doc }: { id: string; doc: LevelDoc }) {
  const s = useEditor();
  const advanced = useSettings((x) => x.advanced);
  const b = doc.bodies.find((x) => x.id === id);
  if (!b) return <div className="panel-pad muted">Not found</div>;
  const name = entityNamer(doc);
  const upd = (label: string, fn: (b: Draft<Body>) => void) => s.change(label, (d) => { const x = d.bodies.find((q) => q.id === id); if (x) fn(x); });
  const editing = s.container === id;
  const comps = bodyComps(b);
  const curated = comps.filter((c) => SCHEMAS[c.type]?.fields.length);
  const bb = bodyBounds(doc, b);
  const movingSlot = slotsOf(doc, `body:${id}`).find((x) => x.addr.kind === 'moving');
  const controllers = b.behavior === 'controlled' ? controllersOf(doc, id) : [];
  const canMerge = !b.owner && (b.terrains.length > 0 || b.layers.some((l) => l.tiles.length));
  return (
    <div className="inspector">
      <div className="insp-head">
        <TextInput value={b.name} onChange={(v) => upd('Rename', (x) => { x.name = v; })} />
        <span className="muted small">
          {behaviorLabel(b.behavior)}{b.owner ? ` · part of ${name(`obj:${b.owner.object}`)}` : ''}
        </span>
      </div>
      <div className="insp-actions">
        <button className={`btn ${editing ? 'active' : ''}`} onClick={() => s.set({ container: editing ? 'level' : id })}>
          <Icon name={editing ? 'exit' : 'enter'} size={16} /> {editing ? 'Done editing shape' : 'Edit shape & tiles'}
        </button>
        {movingOf(b) && <button className="btn" onClick={() => s.set({ tool: 'waypoint' })}><Icon name="waypoint" size={16} /> Add waypoints</button>}
      </div>
      <Section title="Platform">
        <Row label="Type">
          {b.owner ? <span className="muted">{behaviorLabel(b.behavior)}</span> : <Select<BodyBehavior> value={b.behavior} options={BEHAVIOR_LABELS} onChange={(v) => setBehavior(id, v)} />}
        </Row>
        <Row label="Drawn" title="Behind the level tiles or in front of them">
          <Select
            value={b.placement}
            options={[['behindLevel', 'Behind the level'], ['coveringLevel', 'In front of the level']]}
            onChange={(v) =>
              upd('Change placement', (x) => {
                x.placement = v;
                const map: Record<string, Record<string, string>> = {
                  behindLevel: { level: 'Moving Platform', foreground: 'Moving Platform FG', background: 'Moving Platform BG' },
                  coveringLevel: { level: 'Moving Platform Covering Level', foreground: 'Moving Platform Covering Level FG', background: 'Moving Platform Covering Level BG' },
                };
                for (const l of x.layers) if (map[v][l.role]) l.sortingLayer = map[v][l.role];
              })
            }
          />
        </Row>
        <Row label="Position">
          <span className="vec">
            <NumberInput value={b.origin[0]} width={70} onChange={(v) => upd('Move', (x) => { x.origin = [v, x.origin[1]]; })} />
            <NumberInput value={b.origin[1]} width={70} onChange={(v) => upd('Move', (x) => { x.origin = [x.origin[0], v]; })} />
          </span>
        </Row>
        {(advanced || b.behavior === 'rotating' || b.rotation) && (
          <Row label={b.behavior === 'rotating' ? 'Start angle' : 'Rotation'}>
            <NumberInput value={b.rotation ?? 0} step={15} onChange={(v) => upd('Rotate', (x) => { x.rotation = v || undefined; })} />
          </Row>
        )}
        <Row label="Hidden in editor">
          <Check value={!!b.hidden} onChange={(v) => upd('Toggle visibility', (x) => { x.hidden = v || undefined; })} />
        </Row>
        <div className="muted small">{bb ? `${bb[2] - bb[0]}×${bb[3] - bb[1]} tiles` : 'empty'} · {b.terrains.length} shapes · {b.objects.length} riding objects</div>
      </Section>
      {movingOf(b) && b.moving && movingSlot && (
        <>
          <Section title="Movement">
            <LinkList doc={doc} slot={{ ...movingSlot, label: 'Activators' }} hint="Empty: moves on its own" />
            {b.moving.activators.length > 1 && (
              <Row label="Moves when">
                <Select value={b.moving.activationType} options={ACTIVATION} onChange={(v) => upd('Activation', (x) => { x.moving!.activationType = v; })} />
              </Row>
            )}
            <Row label="When turned off">
              <Select value={b.moving.deactivationAction} options={DEACTIVATION} onChange={(v) => upd('When turned off', (x) => { x.moving!.deactivationAction = v; })} />
            </Row>
            <Row label="At the end">
              <Select value={b.moving.finishAction} options={FINISH} onChange={(v) => upd('At the end', (x) => { x.moving!.finishAction = v; })} />
            </Row>
            <Row label="Start is a stop" title="The start position is part of the path (returns there when looping)">
              <Check value={!b.moving.dontUseStartAsMovementPoint} onChange={(v) => upd('Start point', (x) => { x.moving!.dontUseStartAsMovementPoint = !v; })} />
            </Row>
            <VarsEditor vars={b.moving.defaultMovementVars} onChange={(k, v) => upd('Movement', (x) => { x.moving!.defaultMovementVars[k] = v; })} />
            <Row label="Preview">
              <input type="range" min={0} max={1} step={0.01} value={s.scrub} onChange={(e) => s.set({ scrub: Number(e.target.value) })} />
            </Row>
          </Section>
          <Section title={`Path (${b.moving.waypoints.length + 1} points)`}>
            <div className="row clickable muted">
              <span className="row-label">Start</span>
              <span className="row-value">{b.origin[0]}, {b.origin[1]}</span>
            </div>
            {b.moving.waypoints.map((w, i) => (
              <div key={w.id} className="row clickable" onClick={() => s.set({ selection: { kind: 'waypoint', body: id, id: w.id } })}>
                <span className="row-label">{i + 1}. {w.name}</span>
                <span className="row-value muted">{w.x}, {w.y}{w.pauseOnArrivalForSeconds ? ` · waits ${w.pauseOnArrivalForSeconds}s` : ''}</span>
              </div>
            ))}
            <button
              className="btn"
              onClick={() =>
                upd('Add waypoint', (x) => {
                  const last = x.moving!.waypoints[x.moving!.waypoints.length - 1] ?? { x: x.origin[0], y: x.origin[1] };
                  x.moving!.waypoints.push(newWaypoint(last.x + 3, last.y, x.moving!.waypoints.length));
                })
              }
            >
              <Icon name="plus" size={14} /> Point
            </button>
          </Section>
          <Section title="At the start point" open={false}>
            <Row label="Pause here (s)">
              <NumberInput value={b.moving.start.pauseOnArrivalForSeconds} step={0.1} min={0} onChange={(v) => upd('Pause', (x) => { x.moving!.start.pauseOnArrivalForSeconds = v; })} />
            </Row>
            <Row label="Stop until turned on again">
              <Check value={b.moving.start.pausePlatformOnArrival} onChange={(v) => upd('Pause', (x) => { x.moving!.start.pausePlatformOnArrival = v; })} />
            </Row>
            <Row label="Teleport to the next point">
              <Check value={b.moving.start.warpToNextPoint} onChange={(v) => upd('Warp', (x) => { x.moving!.start.warpToNextPoint = v; })} />
            </Row>
            <VarsEditor vars={b.moving.start.movementVars} onChange={(k, v) => upd('Start point', (x) => { x.moving!.start.movementVars[k] = v; })} />
          </Section>
        </>
      )}
      {curated.map((c) => (
        <Section key={c.type} title={SCHEMAS[c.type].title}>
          <CompFields doc={doc} comp={c} />
          {c.type === 'RigidBodyRotator' && (
            <div className="insp-actions">
              <button className="btn" title="Rotate around the middle of the platform" onClick={() => recentreBody(id)}>Centre the pivot</button>
            </div>
          )}
        </Section>
      ))}
      {b.behavior === 'controlled' && (
        <Section title={`Controllers (${controllers.length})`}>
          {!controllers.length && <div className="muted small">Nothing steers this block yet.</div>}
          <ControllerRows doc={doc} rows={controllers} />
          <button className="btn" onClick={() => { const c = addController(id); if (c) s.set({ selection: { kind: 'objects', ids: [c] } }); }}>
            <Icon name="plus" size={14} /> Controller & control panel
          </button>
        </Section>
      )}
      <LinkedFrom doc={doc} ent={`body:${id}`} />
      {advanced && (
        <>
          <div className="adv-head">Raw Unity fields</div>
          {comps.map((c) => (
            <Section key={c.type} title={c.type} open={false}>
              <FieldEditor data={c.data} entityName={name} onSet={(p, v) => s.change(`Edit ${c.type}`, (d) => writeField(d, c.addr, p, v))} />
            </Section>
          ))}
          {(b.components ?? []).map((c, i) =>
            c.type === 'Rigidbody2D' ? (
              <Section key={`rb${i}`} title="Rigidbody2D" open={false}>
                <FieldEditor data={c.data} entityName={name} onSet={(p, v) => s.change('Edit Rigidbody2D', (d) => { const x = d.bodies.find((q) => q.id === id)?.components?.[i]; if (x && v) writeCustom(x.data, p, v); })} />
              </Section>
            ) : null,
          )}
        </>
      )}
      <div className="insp-actions">
        {canMerge && <button className="btn" title="Put its shapes and tiles back into the level" onClick={() => bodyToLevel(id)}>Move back to level</button>}
        <button className="btn danger" onClick={() => deleteSelection({ kind: 'body', id })} disabled={!!b.owner}>
          Delete
        </button>
      </div>
    </div>
  );
}

function writeCustom(data: any, p: string, v: { v?: string; r?: string }) {
  const keys = p.split('.');
  let cur = data;
  for (const k of keys.slice(0, -1)) cur = cur[k] ??= {};
  cur[keys[keys.length - 1]] = v.r !== undefined ? (v.r ? { fileID: '1', ref: v.r } : { fileID: '0' }) : v.v ?? '';
}

function WaypointInspector({ bodyId, id, doc }: { bodyId: string; id: string; doc: LevelDoc }) {
  const change = useEditor((s) => s.change);
  const b = doc.bodies.find((x) => x.id === bodyId);
  const w = b?.moving?.waypoints.find((x) => x.id === id);
  if (!b || !w) return <div className="panel-pad muted">Waypoint not found</div>;
  const idx = b.moving!.waypoints.indexOf(w);
  const upd = (label: string, fn: (w: Draft<Waypoint>) => void) =>
    change(label, (d) => { const x = d.bodies.find((q) => q.id === bodyId)?.moving?.waypoints.find((q) => q.id === id); if (x) fn(x); });
  const move = (dir: -1 | 1) =>
    change('Reorder waypoint', (d) => {
      const list = d.bodies.find((q) => q.id === bodyId)!.moving!.waypoints;
      const i = list.findIndex((q) => q.id === id);
      const j = i + dir;
      if (j < 0 || j >= list.length) return;
      [list[i], list[j]] = [list[j], list[i]];
    });
  return (
    <div className="inspector">
      <div className="insp-head">
        <TextInput value={w.name} onChange={(v) => upd('Rename waypoint', (x) => { x.name = v; })} />
        <span className="muted small">Point {idx + 1} of {b.name}</span>
      </div>
      <div className="insp-actions">
        <button className="btn" onClick={() => useEditor.getState().set({ selection: { kind: 'body', id: bodyId } })}>← Platform</button>
        <button className="btn" onClick={() => move(-1)} disabled={idx === 0}><Icon name="up" size={14} /></button>
        <button className="btn" onClick={() => move(1)} disabled={idx === b.moving!.waypoints.length - 1}><Icon name="down" size={14} /></button>
      </div>
      <Section title="Point">
        <Row label="Position">
          <span className="vec">
            <NumberInput value={w.x} width={70} onChange={(v) => upd('Move waypoint', (x) => { x.x = v; })} />
            <NumberInput value={w.y} width={70} onChange={(v) => upd('Move waypoint', (x) => { x.y = v; })} />
          </span>
        </Row>
        <Row label="Pause here (s)">
          <NumberInput value={w.pauseOnArrivalForSeconds} step={0.1} min={0} onChange={(v) => upd('Pause', (x) => { x.pauseOnArrivalForSeconds = v; })} />
        </Row>
        <Row label="Stop until turned on again">
          <Check value={w.pausePlatformOnArrival} onChange={(v) => upd('Pause', (x) => { x.pausePlatformOnArrival = v; })} />
        </Row>
        <Row label="Teleport to the next point">
          <Check value={w.warpToNextPoint} onChange={(v) => upd('Warp', (x) => { x.warpToNextPoint = v; })} />
        </Row>
      </Section>
      <Section title="Moving to this point">
        <VarsEditor vars={w.movementVars} onChange={(k, v) => upd('Movement', (x) => { x.movementVars[k] = v; })} />
      </Section>
      <div className="insp-actions">
        <button className="btn danger" onClick={() => deleteSelection({ kind: 'waypoint', body: bodyId, id })}>Delete point</button>
      </div>
    </div>
  );
}

/** "Make moving platform" for a level shape, or the way back for a shape inside a body. */
function ConvertShape({ doc, t, container }: { doc: LevelDoc; t: TerrainShape; container: string }) {
  const [kind, setKind] = useState<BodyBehavior>('movingPlatform');
  const takeObjects = useSettings((s) => s.convertTakesObjects);
  const update = useSettings((s) => s.update);
  const st = useEditor();
  if (container !== 'level') {
    const b = doc.bodies.find((x) => x.id === container);
    return (
      <Section title="Platform">
        <div className="muted small">This shape is part of <b>{b?.name}</b> ({b ? behaviorLabel(b.behavior) : ''}).</div>
        <div className="insp-actions">
          <button className="btn" onClick={() => st.set({ selection: { kind: 'body', id: container }, container: 'level' })}>Select platform</button>
          {b && !b.owner && <button className="btn" onClick={() => bodyToLevel(container)}>Move back to level</button>}
        </div>
      </Section>
    );
  }
  if (t.mode !== 'add') return null;
  return (
    <Section title="Make it move">
      <Row label="Turn into">
        <Select<BodyBehavior> value={kind} options={BEHAVIOR_LABELS} onChange={setKind} />
      </Row>
      <Row label="Take objects on it" title="Coins, switches and other objects standing on or inside the shape ride along">
        <Check value={takeObjects} onChange={(v) => update({ convertTakesObjects: v })} />
      </Row>
      <button className="btn primary block" onClick={() => shapeToBody(t.id, kind, takeObjects)}>
        <Icon name="detach" size={16} /> Make {behaviorLabel(kind).toLowerCase()}
      </button>
    </Section>
  );
}

/** Missing-tile cells of the container within a shape's bounds and style. */
function missingIn(doc: LevelDoc, cont: ReturnType<typeof getContainer>, t: TerrainShape): number {
  const style = t.style.split(':')[0];
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of t.rings[0] ?? []) {
    x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  }
  return terrainMissing(doc, cont).filter((m) => m.style === style && m.x >= x0 - 1 && m.x < x1 + 1 && m.y >= y0 - 1 && m.y < y1 + 1).length;
}

/** Density slider positions: 0 = off, POS_ONE = ×1, each POS_DOUBLE steps doubles it (up to ×64). */
const POS_ONE = 40, POS_DOUBLE = 10, POS_MAX = 100;
const posToDensity = (p: number) => (p <= 0 ? 0 : Math.round(2 ** ((p - POS_ONE) / POS_DOUBLE) * 1000) / 1000);
const densityToPos = (v: number) => (v <= 0 ? 0 : Math.max(1, Math.min(POS_MAX, Math.round(POS_ONE + POS_DOUBLE * Math.log2(v)))));
const densityLabel = (v: number) => (v <= 0 ? 'Off' : `×${v < 1 ? v.toFixed(2) : v < 10 ? v.toFixed(1) : Math.round(v)}`);

/** Log-scale density multiplier; one drag is one undo step. */
function DensitySlider({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const token = useRef<object | null>(null);
  const end = () => {
    if (!token.current) return;
    token.current = null;
    setActiveInput(null);
  };
  useEffect(() => end, []);
  return (
    <span className="inline density">
      <input
        type="range"
        min={0}
        max={POS_MAX}
        step={1}
        value={densityToPos(value)}
        onPointerDown={() => {
          token.current = {};
          setActiveInput(token.current, true);
        }}
        onPointerUp={end}
        onBlur={end}
        onChange={(e) => onChange(posToDensity(Number(e.target.value)))}
      />
      <span className="density-value" onDoubleClick={() => onChange(1)} title="Double-click: back to ×1">{densityLabel(value)}</span>
    </span>
  );
}

function TerrainInspector({ id, doc }: { id: string; doc: LevelDoc }) {
  const change = useEditor((s) => s.change);
  const advanced = useSettings((s) => s.advanced);
  const f = findTerrain(doc, id);
  if (!f) return <div className="panel-pad muted">Shape not found</div>;
  const t = f.terrain;
  const cont = getContainer(doc, f.container);
  const upd = (label: string, fn: (t: Draft<TerrainShape>) => void) =>
    change(label, (d) => {
      const list = f.container === 'level' ? d.terrains : d.bodies.find((b) => b.id === f.container)!.terrains;
      const x = list.find((q) => q.id === id);
      if (x) fn(x);
    });
  const styles = terrainStyles();
  const style = styles.find((s) => s.id === t.style);
  const problems = validateShape(t.style, t.rings);
  const missing = missingIn(doc, cont, t);
  const layerOpts: [string, string][] = [['', '(none)'], ...cont.layers.map((l) => [l.id, advanced ? `${l.name} (${l.sortingLayer})` : l.name] as [string, string])];
  const reorder = (dir: -1 | 1) =>
    change('Reorder shape', (d) => {
      const list = f.container === 'level' ? d.terrains : d.bodies.find((b) => b.id === f.container)!.terrains;
      const i = list.findIndex((q) => q.id === id);
      const j = i + dir;
      if (j < 0 || j >= list.length) return;
      [list[i], list[j]] = [list[j], list[i]];
    });
  return (
    <div className="inspector">
      <div className="insp-head">
        <TextInput value={t.name ?? ''} placeholder="Terrain shape" onChange={(v) => upd('Rename shape', (x) => { x.name = v || undefined; })} />
        <span className="muted small">{t.rings[0]?.length ?? 0} points · {t.rings.length - 1} holes · {f.container === 'level' ? 'level' : 'in a platform'}</span>
      </div>
      <Section title="Shape">
        <Row label="Style">
          <Select value={t.style} options={styles.map((s) => [s.id, s.name] as [string, string])} onChange={(v) => upd('Change style', (x) => { x.style = v; })} />
        </Row>
        {style && style.variants.length > 1 && (
          <Row label="Variant">
            <Select
              value={t.variant ?? style.variants[0]}
              options={style.variants.map((v) => [v, v] as [string, string])}
              onChange={(v) => upd('Change variant', (x) => { x.variant = v; })}
            />
          </Row>
        )}
        <Row label="Mode">
          <Select value={t.mode} options={[['add', 'Add'], ['subtract', 'Cut']]} onChange={(v) => upd('Change mode', (x) => { x.mode = v; })} />
        </Row>
        <Row label="Hidden">
          <Check value={!!t.hidden} onChange={(v) => upd('Toggle shape', (x) => { x.hidden = v || undefined; })} />
        </Row>
        <div className="insp-actions">
          <button className="btn" onClick={() => reorder(-1)}>Send back</button>
          <button className="btn" onClick={() => reorder(1)}>Bring forward</button>
          {t.rings.length > 1 && <button className="btn" onClick={() => upd('Remove holes', (x) => { x.rings = x.rings.slice(0, 1); })}>Remove holes</button>}
        </div>
      </Section>
      <ConvertShape doc={doc} t={t} container={f.container} />
      {advanced && (
        <Section title="Output layers">
          <Row label="Ground (level)">
            <Select value={t.targets.level} options={layerOpts.slice(1)} onChange={(v) => upd('Target layer', (x) => { x.targets.level = v; })} />
          </Row>
          <Row label="Foreground">
            <Select value={t.targets.foreground ?? ''} options={layerOpts} onChange={(v) => upd('Target layer', (x) => { x.targets.foreground = v || undefined; })} />
          </Row>
          <Row label="Background">
            <Select value={t.targets.background ?? ''} options={layerOpts} onChange={(v) => upd('Target layer', (x) => { x.targets.background = v || undefined; })} />
          </Row>
        </Section>
      )}
      <Section title="Dark shading" open={false}>
        <Row label="Enabled">
          <Check value={t.shading.enabled} onChange={(v) => upd('Shading', (x) => { x.shading = { ...x.shading, enabled: v }; })} />
        </Row>
        <Row label="Depth">
          <NumberInput value={t.shading.depth} min={0} max={20} onChange={(v) => upd('Shading', (x) => { x.shading = { ...x.shading, depth: v }; })} />
        </Row>
        <Row label="Jitter">
          <input type="range" min={0} max={1} step={0.05} value={t.shading.jitter} onChange={(e) => upd('Shading', (x) => { x.shading = { ...x.shading, jitter: Number(e.target.value) }; })} />
        </Row>
        <Row label="Seed">
          <span className="inline">
            <NumberInput value={t.shading.seed} onChange={(v) => upd('Shading', (x) => { x.shading = { ...x.shading, seed: v }; })} />
            <button className="mini" onClick={() => upd('Shading', (x) => { x.shading = { ...x.shading, seed: Math.floor(Math.random() * 1e6) }; })}>⟳</button>
          </span>
        </Row>
      </Section>
      <Section title="Decoration" open={false}>
        <Row label="Grass / edge">
          <Select<EdgeArtMode>
            value={t.decoration.edge ?? 'auto'}
            options={[['auto', 'Auto'], ['full', 'Full'], ['none', 'None']]}
            onChange={(v) => upd('Grass', (x) => { x.decoration = { ...x.decoration, edge: v }; })}
          />
        </Row>
        <Row label="Enabled">
          <Check value={t.decoration.enabled} onChange={(v) => upd('Decoration', (x) => { x.decoration = { ...x.decoration, enabled: v }; })} />
        </Row>
        <Row label="Density (all)" title="Master density for this polygon only. It multiplies the chance of every decoration on the selected shape; Off removes all of its foliage, painted foliage too.">
          <DensitySlider value={t.decoration.density} onChange={(v) => upd('Decoration density', (x) => { x.decoration = { ...x.decoration, density: v }; })} />
        </Row>
        {FOLIAGE_KINDS.filter((k) => style?.foliage[k]).map((k) => (
          <Row key={k} label={FOLIAGE_LABELS[k]} title={`${style!.foliage[k]} ${FOLIAGE_LABELS[k].toLowerCase()} decorations in this style; on top of the master density, for this polygon only. Off removes them, painted ones too.`}>
            <DensitySlider
              value={t.decoration.kinds?.[k] ?? 1}
              onChange={(v) =>
                upd(`${FOLIAGE_LABELS[k]} density`, (x) => {
                  const kinds = { ...x.decoration.kinds };
                  if (v === 1) delete kinds[k];
                  else kinds[k] = v;
                  const decoration = { ...x.decoration };
                  if (Object.keys(kinds).length) decoration.kinds = kinds;
                  else delete decoration.kinds;
                  x.decoration = decoration;
                })
              }
            />
          </Row>
        ))}
        <Row label="Foreground">
          <Check value={t.decoration.foreground} onChange={(v) => upd('Decoration', (x) => { x.decoration = { ...x.decoration, foreground: v }; })} />
        </Row>
        <Row label="Background">
          <Check value={t.decoration.background} onChange={(v) => upd('Decoration', (x) => { x.decoration = { ...x.decoration, background: v }; })} />
        </Row>
        <Row label="Seed">
          <span className="inline">
            <NumberInput value={t.decoration.seed} onChange={(v) => upd('Decoration', (x) => { x.decoration = { ...x.decoration, seed: v }; })} />
            <button className="mini" onClick={() => upd('Decoration', (x) => { x.decoration = { ...x.decoration, seed: Math.floor(Math.random() * 1e6) }; })}>⟳</button>
          </span>
        </Row>
      </Section>
      {missing > 0 && (
        <Section title={`Missing tiles (${missing})`}>
          <div className="row small warn">
            {missing === 1 ? '1 cell is' : `${missing} cells are`} on a slope the {style?.name ?? t.style} art has no tile for (marked pink). Pick sprites for them with the Fix tool, or draw them (Tileset tool → Tile cases).
          </div>
          <div className="insp-actions">
            <button className="btn" onClick={() => useEditor.getState().set({ tool: 'fix' })}><Icon name="fix" size={16} /> Fix tool</button>
            <button className="btn" onClick={() => openTileset(t.style)}><Icon name="cases" size={16} /> All tile cases</button>
          </div>
        </Section>
      )}
      {problems.length > 0 && (
        <Section title={`Problems (${problems.length})`}>
          {problems.slice(0, 30).map((p, i) => (
            <div key={i} className="row small warn">{p.x},{p.y}: {p.message}</div>
          ))}
        </Section>
      )}
      <div className="insp-actions">
        <button className="btn danger" onClick={() => deleteSelection({ kind: 'terrain', id })}>Delete shape</button>
      </div>
    </div>
  );
}

export { ColorInput };
